// src/pages/EditCarrierProfile.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { uploadProfilePhoto } from '../services/firestoreService';
import ChangePassword from '../components/ChangePassword';
import ChangeEmail from '../components/ChangeEmail';
import DeleteAccountRequest from '../components/DeleteAccountRequest';
import BrandName from '../components/BrandName';
import RegisterCheck from '../components/RegisterCheck';
import app from '../firebase';
import './EditCarrierProfile.css';

const functions = getFunctions(app);

const VEHICLE_TYPES = ['Small Van', 'Large Van', 'Pickup Truck', 'Luton Van', 'Skip Lorry'];
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

const EditCarrierProfile = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    bio:         user?.bio         || '',
    phoneNumber: user?.phoneNumber || '',
    vehicle:     user?.vehicle     || '',
    location:    user?.location    || '',
  });
  const [saving,        setSaving]        = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress,  setPhotoProgress]  = useState(0);
  const [photoSuccess,   setPhotoSuccess]   = useState(false);
  const [photoError,     setPhotoError]     = useState('');
  const [previewUrl,     setPreviewUrl]     = useState(user?.profilePhoto || null);

  const [stripeLoading,    setStripeLoading]    = useState(false);
  const [stripeError,      setStripeError]      = useState('');
  const [stripeRefreshing, setStripeRefreshing] = useState(false);
  const [stripeRefreshMsg, setStripeRefreshMsg] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setPhotoError('Photo must be under 2 MB.');
      return;
    }
    setPhotoError('');
    setPhotoSuccess(false);
    setPhotoUploading(true);
    setPhotoProgress(0);
    // Show local preview immediately
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const url = await uploadProfilePhoto(user.uid, file, setPhotoProgress);
      await updateUser({ profilePhoto: url });
      setPreviewUrl(url);
      setPhotoSuccess(true);
    } catch (err) {
      setPhotoError('Upload failed. Please try again.');
      console.error('[EditCarrierProfile] photo upload error:', err);
    }
    setPhotoUploading(false);
    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await updateUser(form);
    setSaving(false);
    setSaved(true);
    setTimeout(() => navigate('/provider-dashboard'), 1200);
  };

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    setStripeError('');
    setStripeRefreshMsg('');
    try {
      const createConnectAccount = httpsCallable(functions, 'createConnectAccount');
      const result = await createConnectAccount();
      if (result.data?.accountId) {
        // The function has already saved the Stripe ids; clients can no longer write them.
        await refreshUser();
      }
      if (result.data?.onboardingUrl) {
        window.location.href = result.data.onboardingUrl;
        return;
      }
      setStripeError('No onboarding URL returned. Please try again.');
    } catch (err) {
      console.error('[EditCarrierProfile] Stripe Connect error:', err);
      setStripeError(err.message || 'Could not start Stripe onboarding. Please try again.');
    }
    setStripeLoading(false);
  };

  const [stripeDashLoading, setStripeDashLoading] = useState(false);
  const handleStripeDashboard = async () => {
    setStripeDashLoading(true);
    setStripeError('');
    // Open the tab synchronously (inside the click) so popup blockers allow it
    const win = window.open('', '_blank');
    try {
      const createStripeLoginLink = httpsCallable(functions, 'createStripeLoginLink');
      const result = await createStripeLoginLink();
      const url = result.data?.url;
      if (!url) throw new Error('No dashboard link returned. Please try again.');
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      if (win && !win.closed) win.close();
      console.error('[EditCarrierProfile] Stripe dashboard error:', err);
      setStripeError(err.message || 'Could not open Stripe dashboard. Please try again.');
    }
    setStripeDashLoading(false);
  };

  const handleStripeRefresh = async () => {
    setStripeRefreshing(true);
    setStripeRefreshMsg('');
    setStripeError('');
    try {
      const refreshStripeStatus = httpsCallable(functions, 'refreshStripeStatus');
      const result = await refreshStripeStatus();
      if (result.data?.accountReset) {
        await refreshUser();
        setStripeRefreshMsg(result.data.reason || 'Please reconnect Stripe to receive payments.');
      } else if (result.data?.onboardingComplete) {
        await refreshUser();
        setStripeRefreshMsg('✅ Stripe account ready! You can now receive payments.');
      } else {
        setStripeRefreshMsg('Onboarding not complete yet. Please continue Stripe onboarding.');
      }
    } catch (err) {
      console.error('[EditCarrierProfile] Stripe refresh error:', err);
      setStripeError(err.message || 'Could not refresh Stripe status. Please try again.');
    }
    setStripeRefreshing(false);
  };

  // On load, silently check the saved Stripe account still exists. If it doesn't (e.g. it
  // was created with test keys), the server resets it and we show the Connect button again.
  useEffect(() => {
    if (!user?.stripeAccountId) return;
    let cancelled = false;
    httpsCallable(functions, 'refreshStripeStatus')()
      .then(async (result) => {
        if (cancelled || !result.data?.accountReset) return;
        await refreshUser();
        setStripeRefreshMsg(result.data.reason || 'Please reconnect Stripe to receive payments.');
      })
      .catch(err => console.warn('[EditCarrierProfile] Stripe status check failed:', err?.message));
    return () => { cancelled = true; };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from the carrier dashboard banner: /provider-profile/edit#payment-setup
  useEffect(() => {
    if (window.location.hash !== '#payment-setup') return;
    const t = setTimeout(() => {
      document.getElementById('payment-setup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  const stripeComplete = user?.stripeOnboardingComplete === true;
  const hasStripeAccount = !!user?.stripeAccountId;

  return (
    <div className="ecp-page">
      <div className="ecp-card">
        <div className="ecp-logo"><BrandName /></div>
        <h2>Edit Carrier Profile</h2>
        <p className="ecp-sub">Keep your profile up to date so customers can trust you</p>

        {/* PROFILE PHOTO */}
        <div className="ecp-section-label">Profile Photo</div>
        <div className="ecp-photo-section">
          <div className="ecp-avatar-wrap">
            {previewUrl
              ? <img src={previewUrl} alt="Profile" className="ecp-avatar-img" />
              : <div className="ecp-avatar-placeholder">{user?.name?.charAt(0) || '?'}</div>
            }
          </div>
          <div className="ecp-photo-controls">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={photoUploading}
            >
              {photoUploading ? `Uploading… ${photoProgress}%` : '📷 Change Photo'}
            </button>
            <p className="ecp-photo-hint">JPG or PNG, max 2 MB</p>
            {photoSuccess && <p className="ecp-photo-success">✓ Photo updated</p>}
            {photoError  && <p className="ecp-photo-error">{photoError}</p>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
        </div>

        {/* LOCKED FIELDS */}
        <div className="ecp-section-label" style={{ marginTop: '24px' }}>Account Details</div>
        <div className="ecp-locked-group">
          <div className="ecp-locked-field">
            <label className="ecp-label">Business / Display Name</label>
            <div className="ecp-locked-value">{user?.name || '—'}</div>
          </div>
          <div className="ecp-locked-field">
            <label className="ecp-label">Waste carrier licence number</label>
            <div className="ecp-locked-value">{user?.licenceNo || '—'}</div>
            <p className="ecp-photo-hint"><RegisterCheck /></p>
          </div>
          <div className="ecp-locked-notice">
            🔒 To change these details, contact{' '}
            <a href="mailto:info@thewastegame.co.uk" className="ecp-contact-link">
              info@thewastegame.co.uk
            </a>
          </div>
        </div>

        {/* EDITABLE FIELDS */}
        <div className="ecp-section-label" style={{ marginTop: '24px' }}>Your Details</div>
        <form onSubmit={handleSubmit}>
          <div className="ecp-form-group">
            <label className="ecp-label">Location</label>
            <input
              className="ecp-input"
              name="location"
              placeholder="e.g. Swansea, SA1"
              value={form.location}
              onChange={handleChange}
            />
          </div>
          <div className="ecp-form-group">
            <label className="ecp-label">Phone Number</label>
            <input
              className="ecp-input"
              name="phoneNumber"
              type="tel"
              placeholder="e.g. 07700 900123"
              value={form.phoneNumber}
              onChange={handleChange}
            />
          </div>
          <div className="ecp-form-group">
            <label className="ecp-label">Vehicle Type</label>
            <select
              className="ecp-select"
              name="vehicle"
              value={form.vehicle}
              onChange={handleChange}
            >
              <option value="">Select vehicle...</option>
              {VEHICLE_TYPES.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="ecp-form-group">
            <label className="ecp-label">About Your Service</label>
            <textarea
              className="ecp-textarea"
              name="bio"
              rows={4}
              placeholder="e.g. Fully licensed, 5+ years experience, specialist in garden clearance..."
              value={form.bio}
              onChange={handleChange}
            />
          </div>

          <div className="ecp-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/provider-dashboard')}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || saved}
            >
              {saved ? '✓ SAVED' : saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </form>

        <ChangePassword />
        <ChangeEmail />

        {/* PAYMENT SETUP — Stripe Connect States A / B / C (no UID gate) */}
        <div className="ecp-stripe-section" id="payment-setup">
          <div className="ecp-section-label">💳 Payment Setup</div>

          {stripeComplete ? (
            <div className="ecp-stripe-state ecp-stripe-done">
              <p className="ecp-stripe-msg">✅ Stripe connected and ready — you can receive payments.</p>
              {stripeError && <p className="ecp-stripe-error">{stripeError}</p>}
              <button
                type="button"
                className="btn btn-primary ecp-stripe-btn"
                onClick={handleStripeDashboard}
                disabled={stripeDashLoading || stripeLoading}
              >
                {stripeDashLoading ? 'Opening…' : '📊 View Stripe dashboard'}
              </button>
              <button
                type="button"
                className="btn btn-outline ecp-stripe-btn"
                onClick={handleStripeConnect}
                disabled={stripeLoading}
              >
                {stripeLoading ? 'Loading…' : '🔁 Reconnect Stripe'}
              </button>
            </div>
          ) : hasStripeAccount ? (
            <div className="ecp-stripe-state">
              <p className="ecp-stripe-msg ecp-stripe-warn">
                ⚠️ Your Stripe account setup is incomplete. Continue onboarding to start receiving payments.
              </p>
              {stripeError && <p className="ecp-stripe-error">{stripeError}</p>}
              {stripeRefreshMsg && <p className="ecp-stripe-refresh-msg">{stripeRefreshMsg}</p>}
              <button
                type="button"
                className="btn btn-primary ecp-stripe-btn"
                onClick={handleStripeConnect}
                disabled={stripeLoading || stripeRefreshing}
              >
                {stripeLoading ? 'Loading…' : 'Continue Stripe Onboarding'}
              </button>
              <button
                type="button"
                className="btn btn-outline ecp-stripe-btn ecp-stripe-refresh-btn"
                onClick={handleStripeRefresh}
                disabled={stripeLoading || stripeRefreshing}
              >
                {stripeRefreshing ? 'Refreshing…' : '🔄 Refresh Stripe Status'}
              </button>
            </div>
          ) : (
            <div className="ecp-stripe-state">
              <p className="ecp-stripe-msg">
                Connect a Stripe account to receive payments from customers. Payments go straight into your own Stripe account: Stripe deducts its card processing fees (UK cards 1.5% + 20p) and WasteGame takes a 5% platform commission.
              </p>
              {stripeError && <p className="ecp-stripe-error">{stripeError}</p>}
              <button
                type="button"
                className="btn btn-primary ecp-stripe-btn"
                onClick={handleStripeConnect}
                disabled={stripeLoading}
              >
                {stripeLoading ? 'Loading…' : 'Connect Stripe Account'}
              </button>
            </div>
          )}
        </div>

        <DeleteAccountRequest />
      </div>
    </div>
  );
};

export default EditCarrierProfile;
